import React, { useEffect, createElement } from 'react';
interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string;
  ogType?: string;
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown>;
  faq?: Array<{
    question: string;
    answer: string;
  }>;
}
function setMetaTag(name: string, content: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
function setJsonLd(data: Record<string, unknown>) {
  const id = 'seo-json-ld';
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
function removeJsonLd() {
  const el = document.getElementById('seo-json-ld');
  if (el) el.remove();
}
const BASE_URL = 'https://viridiaanalytics.com';
export function SEOHead({
  title,
  description,
  canonical,
  keywords,
  ogType = 'website',
  ogImage = `${BASE_URL}/og-image.png`,
  noindex = false,
  jsonLd,
  faq
}: SEOHeadProps) {
  useEffect(() => {
    // Title
    document.title = title;
    // Core meta
    setMetaTag('description', description);
    setMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    if (keywords) {
      setMetaTag('keywords', keywords);
    }
    // Canonical — always strip query parameters
    const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : `${BASE_URL}${window.location.pathname}`;
    setCanonical(canonicalUrl);
    // Open Graph
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:site_name', 'Viridia Analytics', true);
    setMetaTag('og:locale', 'en_US', true);
    // Twitter Card
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', ogImage);
    // Geo tags for local SEO
    setMetaTag('geo.region', 'US-AZ');
    setMetaTag('geo.placename', 'Phoenix');
    // JSON-LD
    let finalJsonLd: any = null;
    if (jsonLd && faq) {
      // If both exist, we can output an array of schemas
      finalJsonLd = [jsonLd, {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map(item => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer
          }
        }))
      }];
    } else if (jsonLd) {
      finalJsonLd = jsonLd;
    } else if (faq) {
      finalJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map(item => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer
          }
        }))
      };
    }
    if (finalJsonLd) {
      setJsonLd(finalJsonLd);
    } else {
      removeJsonLd();
    }
    return () => {
      // Cleanup JSON-LD on unmount
      removeJsonLd();
    };
  }, [title, description, canonical, keywords, ogType, ogImage, noindex, jsonLd, faq]);
  return null;
}
// Pre-built JSON-LD for the business
export const LOCAL_BUSINESS_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [{
    '@type': 'ProfessionalService',
    '@id': `${BASE_URL}/#organization`,
    name: 'Viridia Analytics',
    description: 'Phoenix marketing agency specializing in AI lead conversion, paid ad campaigns, and web design for local home service businesses like HVAC, plumbing, roofing, and landscaping.',
    url: BASE_URL,
    telephone: '+16233000708',
    email: 'info@viridiaanalytics.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Phoenix',
      addressRegion: 'AZ',
      addressCountry: 'US'
    },
    foundingDate: '2025',
    founder: {
      '@id': `${BASE_URL}/#founder`
    },
    areaServed: [{
      '@type': 'City',
      name: 'Phoenix'
    }, {
      '@type': 'City',
      name: 'Scottsdale'
    }, {
      '@type': 'City',
      name: 'Glendale'
    }, {
      '@type': 'City',
      name: 'Peoria'
    }, {
      '@type': 'City',
      name: 'Chandler'
    }, {
      '@type': 'City',
      name: 'Mesa'
    }, {
      '@type': 'City',
      name: 'Queen Creek'
    }, {
      '@type': 'City',
      name: 'Buckeye'
    }, {
      '@type': 'City',
      name: 'Long Beach'
    }, {
      '@type': 'City',
      name: 'Los Angeles'
    }],
    priceRange: '$$',
    sameAs: ['https://www.facebook.com/profile.php?id=61571074363261', 'https://www.instagram.com/viridiaanalyticsllc/', 'https://www.linkedin.com/company/viridia-analytics/', 'https://www.yelp.com/biz/viridia-analytics-phoenix-2'],
    knowsAbout: ['AI lead conversion', 'AI marketing agency', 'AI advertising', 'Yelp advertising', 'Google Ads management', 'local SEO', 'home services marketing', 'contractor marketing', 'HVAC marketing', 'painter marketing', 'landscaper marketing', 'window installer marketing', 'marketing automation', 'CRM systems', 'reputation management'],
    slogan: 'Stop Losing Leads. Start Growing.',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Marketing Services',
      itemListElement: [{
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'AI Lead Conversion',
          description: 'AI-powered lead response, automated follow-up, and lead nurturing systems that respond in under 60 seconds.'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Custom Automations',
          description: 'Bespoke automation workflows and API integrations to connect tools and eliminate manual data entry.'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'CRM & Business Tools',
          description: 'All-in-one management system to centralize leads, conversations, appointments, and invoicing.'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Reputation Management',
          description: 'Automated review requests and management across platforms to build trust and local authority.'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Ad Campaign Management',
          description: 'Google Ads, Meta Ads, and local lead platform management for businesses to drive qualified leads.'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Web Design & SEO',
          description: 'Fast, conversion-focused websites with local SEO optimization to turn searches into customers.'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Google Business Profile Setup',
          description: 'Professional setup, optimization, and verification of Google Business Profiles for local visibility.'
        }
      }]
    }
  }, {
    '@type': 'Person',
    '@id': `${BASE_URL}/#founder`,
    name: 'Kevin Rogers',
    jobTitle: 'Founder & CEO',
    worksFor: {
      '@id': `${BASE_URL}/#organization`
    },
    description: 'Founder of Viridia Analytics. Former shower installer and cabinet finisher turned ad platform insider, specializing in AI-powered lead conversion and digital advertising for local service businesses and contractors.',
    sameAs: ['https://www.linkedin.com/in/kevinrogers-viridia/']
  }]
};